import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Target, Search, Building, Sparkles, Check, X } from 'lucide-react';
import api from '../../lib/api';
import ServiceContextInput from '../../components/ServiceContextInput';
import { useAuth } from '../../context/AuthContext';
import { useJobContext } from '../../context/JobContext';
import { toast } from 'sonner';

export default function ServiceSetup() {
  const { user, checkAuth } = useAuth();
  const { dispatch } = useJobContext();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState('forward');
  const [animating, setAnimating] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [targetLocation, setTargetLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const suggestionTimeout = React.useRef(null);

  const handleServiceBlur = () => {
    const val = serviceName.trim();
    if (val.length < 3) return;
    if (suggestionTimeout.current) clearTimeout(suggestionTimeout.current);
    suggestionTimeout.current = setTimeout(async () => {
      setSuggesting(true);
      try {
        const res = await api.post('/api/profile/suggest-service-context', { input: val });
        if (res.data?.suggestion && res.data.suggestion !== val) {
          setAiSuggestion(res.data.suggestion);
        }
      } catch {} finally {
        setSuggesting(false);
      }
    }, 600);
  };

  const acceptAiSuggestion = () => {
    if (aiSuggestion) {
      setServiceName(aiSuggestion);
      setAiSuggestion(null);
      toast.success('AI suggestion applied!');
    }
  };

  const handleNext = () => {
    if (!serviceName.trim()) {
      toast.error('Please tell us what you offer');
      return;
    }
    if (animating) return;
    setDirection('forward');
    setAnimating(true);
    setTimeout(() => {
      setStep(2);
      setAnimating(false);
    }, 300);
  };

  const handleBack = () => {
    if (animating) return;
    setDirection('back');
    setAnimating(true);
    setTimeout(() => {
      setStep(1);
      setAnimating(false);
    }, 300);
  };

  const handleComplete = async () => {
    {/* AI Suggestion */}
            {aiSuggestion && (
              <div style={{
                marginTop: 12, padding: '14px 18px',
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-primary)30',
                borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12,
                fontSize: 14, animation: 'fadeIn 0.3s ease',
              }}>
                <Sparkles size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>AI Suggested</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{aiSuggestion}</div>
                </div>
                <button onClick={acceptAiSuggestion} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '6px 14px', borderRadius: 8,
                  background: 'var(--accent-primary)', color: 'white',
                  border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}><Check size={12} /> Use This</button>
                <button onClick={() => setAiSuggestion(null)} style={{
                  padding: 4, borderRadius: 6,
                  background: 'transparent', color: 'var(--text-tertiary)',
                  border: 'none', cursor: 'pointer',
                }}><X size={14} /></button>
              </div>
            )}
            {suggesting && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-tertiary)', fontSize: 12 }}>
                <span style={{ width: 14, height: 14, border: '2px solid var(--accent-primary)', borderTop: '2px solid transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                Generating AI suggestion...
              </div>
            )}

    if (!targetCustomer.trim()) {
      toast.error('Please tell us who you want to reach');
      return;
    }

    if (!targetLocation.trim()) {
      toast.error('Please enter your target location');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/api/auth/profile', {
        service_name: serviceName,
        onboarding_complete: true,
      });
      
      await checkAuth();
      
      try {
        const searchResponse = await api.post('/api/search', {
          query: [targetCustomer.trim(), targetLocation.trim()].filter(Boolean).join(' in '),
          limit: 5,
          enrich: true,
          score: true,
          service_context: serviceName,
        });

        if (searchResponse.data?.status === 'started') {
          dispatch({ type: 'JOB_STARTED', job_id: searchResponse.data.job_id, query: `${targetCustomer.trim()} in ${targetLocation.trim()}`, progress: 'Starting search...', progress_percent: 5, started_at: new Date().toISOString() });
        } else if (searchResponse.data?.status === 'queued') {
          dispatch({ type: 'JOB_QUEUED', job_id: searchResponse.data.job_id, query: `${targetCustomer.trim()} in ${targetLocation.trim()}`, queued_at: new Date().toISOString() });
        }

        toast.success('Your first search is running!');
      } catch (err) {
        // Search might fail silently,  that's ok
      }
      
      navigate('/dashboard/new-search');
    } catch (err) {
      toast.error('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-primary)',
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 1, overflowY: 'auto', overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      padding: 'clamp(16px, 4vw, 40px) 16px',
      boxSizing: 'border-box',
    }}>
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOutLeft {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-60px); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOutRight {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(60px); }
        }
        .slide-enter-forward {
          animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .slide-exit-forward {
          animation: slideOutLeft 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .slide-enter-back {
          animation: slideInLeft 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .slide-exit-back {
          animation: slideOutRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'clamp(24px, 5vw, 48px) clamp(20px, 4vw, 48px)',
        width: '100%', maxWidth: 520,
        boxShadow: 'var(--shadow-xl)',
        position: 'relative',
        animation: 'scaleIn 0.4s ease',
        margin: 'auto',
        flexShrink: 0,
      }}>
        {/* Step indicator */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          marginBottom: 'var(--space-2xl)',
        }}>
          <div style={{
            width: step === 1 ? 32 : 10, height: 10, borderRadius: 5,
            background: step === 1 ? 'var(--accent-primary)' : 'var(--border-primary)',
            transition: 'all 0.3s ease',
          }} />
          <div style={{
            width: step === 2 ? 32 : 10, height: 10, borderRadius: 5,
            background: step === 2 ? 'var(--accent-primary)' : 'var(--border-primary)',
            transition: 'all 0.3s ease',
          }} />
        </div>

        {/* Brand header,  static, doesn't animate */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
          <h1 style={{
            fontSize: 'var(--text-2xl)', fontWeight: 700,
            color: 'var(--text-primary)', margin: '0 0 6px',
            letterSpacing: '-0.5px',
          }}>
            <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 32, width: "auto" }} />
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
            Let's set you up for success
          </p>
        </div>

        {/* Animated step container */}
        <div style={{ position: 'relative' }}>
          {/* ================================================================ */}
          {/* STEP 1 */}
          {/* ================================================================ */}
          <div
            className={
              step === 1
                ? (animating ? 'slide-exit-forward' : '')
                : (animating ? 'slide-enter-back' : '')
            }
            style={{
              display: step === 1 ? 'block' : 'none',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--radius-lg)',
              background: 'var(--accent-glow)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto var(--space-lg)',
            }}>
              <Building size={28} color="var(--accent-primary)" />
            </div>

            <h2 style={{
              color: 'var(--text-primary)', fontSize: 'var(--text-lg)',
              fontWeight: 600, textAlign: 'center', margin: '0 0 6px',
            }}>
              What service do you provide?
            </h2>
            <p style={{
              color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)',
              textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5,
            }}>
              Be specific. This helps our AI write better pitch angles for your outreach.
            </p>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block', color: 'var(--text-secondary)',
                fontSize: 'var(--text-sm)', fontWeight: 500,
                marginBottom: 8, textAlign: 'left',
              }} htmlFor="service-name">
                Your service
              </label>
              <ServiceContextInput value={serviceName} onChange={setServiceName} disabled={false} />
            </div>

            <button
              onClick={handleNext}
              disabled={animating || !serviceName.trim()}
              className="auth-button auth-button-primary"
              style={{
                height: 50, fontSize: 'var(--text-base)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, width: '100%',
                opacity: serviceName.trim() ? 1 : 0.5,
                cursor: animating ? 'wait' : 'pointer',
              }}
            >
              Continue <ArrowRight size={18} />
            </button>

            <p style={{
              color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)',
              textAlign: 'center', marginTop: 16,
            }}>
              Takes less than 60 seconds
            </p>
          </div>

          {/* ================================================================ */}
          {/* STEP 2 */}
          {/* ================================================================ */}
          <div
            className={
              step === 2
                ? (animating ? 'slide-exit-back' : '')
                : (animating ? 'slide-enter-forward' : '')
            }
            style={{
              display: step === 2 ? 'block' : 'none',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--radius-lg)',
              background: 'var(--accent-amber-glow)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto var(--space-lg)',
            }}>
              <Target size={28} color="var(--accent-amber)" />
            </div>

            <h2 style={{
              color: 'var(--text-primary)', fontSize: 'var(--text-lg)',
              fontWeight: 600, textAlign: 'center', margin: '0 0 6px',
            }}>
              Who are you trying to reach?
            </h2>
            <p style={{
              color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)',
              textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5,
            }}>
              This becomes your first search. We'll find businesses matching this description.
            </p>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block', color: 'var(--text-secondary)',
                fontSize: 'var(--text-sm)', fontWeight: 500,
                marginBottom: 8, textAlign: 'left',
              }} htmlFor="target-customer">
                Your ideal customer
              </label>
              <input
                id="target-customer"
                type="text"
                value={targetCustomer}
                onChange={(e) => setTargetCustomer(e.target.value)}
                placeholder="e.g., Hotels, Dental Clinics"
                style={{
                  width: '100%', padding: '14px 16px',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)', fontSize: 'var(--text-base)',
                  outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  marginBottom: 16,
                }}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleComplete(); }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent-amber)';
                  e.target.style.boxShadow = '0 0 0 3px var(--accent-amber-glow)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--input-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />

              <label style={{
                display: 'block', color: 'var(--text-secondary)',
                fontSize: 'var(--text-sm)', fontWeight: 500,
                marginBottom: 8, textAlign: 'left',
              }} htmlFor="target-location">
                Target location
              </label>
              <input
                id="target-location"
                type="text"
                value={targetLocation}
                onChange={(e) => setTargetLocation(e.target.value)}
                placeholder="e.g., Lagos, Berlin, Austin"
                style={{
                  width: '100%', padding: '14px 16px',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)', fontSize: 'var(--text-base)',
                  outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleComplete(); }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent-amber)';
                  e.target.style.boxShadow = '0 0 0 3px var(--accent-amber-glow)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--input-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleBack}
                disabled={animating}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '12px 20px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)', background: 'transparent',
                  color: 'var(--text-secondary)', cursor: animating ? 'wait' : 'pointer',
                  fontSize: 'var(--text-sm)', fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                onClick={handleComplete}
                disabled={saving || animating || !targetCustomer.trim() || !targetLocation.trim()}
                className="auth-button auth-button-primary"
                style={{
                  flex: 1, height: 50, fontSize: 'var(--text-base)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8,
                  opacity: targetCustomer.trim() && targetLocation.trim() ? 1 : 0.5,
                }}
              >
                {saving ? (
                  <><span className="spinner" /> Setting up...</>
                ) : (
                  <><Search size={18} /> Find My Leads</>
                )}
              </button>
            </div>

            <p style={{
              color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)',
              textAlign: 'center', marginTop: 16,
            }}>
              You can change your target market anytime in Settings
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
